import Vue from 'vue';
import { join, memoizeWith } from 'ramda';
import { ApolloClient } from '@apollo/client';
import { createHttpLink } from 'apollo-link-http';
import { WebSocketLink } from 'apollo-link-ws';
import { onError } from 'apollo-link-error';
import { InMemoryCache } from 'apollo-cache-inmemory';
import { split, from } from 'apollo-link';
import { setContext } from 'apollo-link-context';
import { getMainDefinition } from 'apollo-utilities';
import { acquireIdToken } from '@/helpers/misc/auth';
import generateRandomNumber from '@/helpers/misc/generateRandomNumber';
import { useFixedBarLoading } from '@/helpers/loadings';
import useEventListener from '@/helpers/misc/useEventListener';
import { hashCode } from '@piri/utils';
import { errorMessages, webSocketConnectionEventName } from './constants';
import AlertError from '@/plugins/piri/error/types/AlertError';

// A flag to prevent 429 error messages from being displayed repeatedly
let isTooManyRequestsErrorShowing = false;
// Timer ID for flag reset
let resetErrorFlagTimeoutId = null;

export const createGraphqlClientBase = memoizeWith((...args) => join('', args), (uri, subscriptionUri) => {
  const { $msal } = Vue.prototype;
  const { emit: emitWebSocketConnectionStatus } = useEventListener(webSocketConnectionEventName);

  const errorLink = onError(({ graphQLErrors, networkError, operation, forward }) => {
    if (graphQLErrors) {
      graphQLErrors.forEach(({ message }) => {
        if (message === errorMessages.USER_IS_NOT_ACTIVE) {
          window.location = '/unauthorized-user';
        }
      });
    }

    if (networkError) {
      const statusCode = networkError?.statusCode;

      if (statusCode === 429 && !isTooManyRequestsErrorShowing) {
        // Lock display error
        isTooManyRequestsErrorShowing = true;

        // Clear previous timer if any
        if (resetErrorFlagTimeoutId) {
          clearTimeout(resetErrorFlagTimeoutId);
        }

        // Unlock after 3 seconds
        resetErrorFlagTimeoutId = setTimeout(() => {
          isTooManyRequestsErrorShowing = false;
        }, 3000);

        throw new AlertError(window.app.$t(window.app.$lang.app.TOO_MANY_REQUESTS));
      }
    }
  });

  const authMiddleware = setContext(async (_, { headers }) => {
    await acquireIdToken($msal);

    // add the authorization to the headers
    return {
      headers: {
        ...headers,
        Authorization: `Bearer ${$msal.data.idToken}`,
        siteid: localStorage.getItem('selectedSiteId'),
      },
    };
  });

  const getSubscriptionConnectionParams = async () => {
    await acquireIdToken($msal);

    return {
      req: {
        headers: {
          authorization: `Bearer ${$msal.data.idToken}`,
          siteid: localStorage.getItem('selectedSiteId'),
        },
      },
    };
  };

  let httpLink = createHttpLink({
    uri,
  });

  if (subscriptionUri) {
    const wsLink = new WebSocketLink({
      uri: subscriptionUri,
      options: {
        lazy: true,
        reconnect: true,
        connectionParams: getSubscriptionConnectionParams,
      },
    });

    wsLink.subscriptionClient.maxConnectTimeGenerator.duration = () => wsLink.subscriptionClient.maxConnectTimeGenerator.max;

    wsLink.subscriptionClient.onDisconnected(() => {
      emitWebSocketConnectionStatus({ disconnected: true });
    });

    wsLink.subscriptionClient.onConnected(() => {
      emitWebSocketConnectionStatus({ disconnected: false });
    });

    wsLink.subscriptionClient.onReconnected(() => {
      emitWebSocketConnectionStatus({ disconnected: false });
    });

    window.ononline = () => {
      wsLink.subscriptionClient.connect();
    };

    window.onoffline = () => {
      wsLink.subscriptionClient.close(false);
    };

    httpLink = split(
      // split based on operation type
      ({ query }) => {
        const definition = getMainDefinition(query);

        return definition.kind === 'OperationDefinition' && definition.operation === 'subscription';
      },
      wsLink,
      authMiddleware.concat(httpLink),
    );
  } else {
    httpLink = authMiddleware.concat(httpLink);
  }

  // Return the created apollo client
  return new ApolloClient({
    link: from([errorLink, httpLink]),
    cache: new InMemoryCache(),
    defaultOptions: {
      query: {
        fetchPolicy: 'no-cache',
        errorPolicy: 'all',
      },
      mutate: {
        fetchPolicy: 'no-cache',
        errorPolicy: 'all',
      },
    },
  });
});

export const createGraphqlClient = () => createGraphqlClientBase(
  process.env.VUE_APP_GRAPHQL_API, process.env.VUE_APP_GRAPHQL_SUBSCRIPTION_API,
);

/**
 * @example
 *
 * query(
 *    gql`
 *      query signatures($filter: SignatureFilterInput!) {
 *          signatures(
 *            filter: $filter
 *          ) {
 *            id
 *            name
 *            sites
 *            isApproved
 *          }
 *      }
 *    `, {
 *     filter: {
 *       sites: ['1'],
 *       isApproved: true,
 *     },
 *   }).then(console.log); */
export const query = (queryString, variables, options = {}) => {
  const randomNumber = generateRandomNumber();
  useFixedBarLoading().show(hashCode(randomNumber));

  return createGraphqlClient().query({
    query: queryString,
    variables,
    ...options,
  })
    .then(result => {
      // errorPolicy: 'all' ile GraphQL hatalari reject etmez; data null/eksik gelir.
      // Sessizce yutulunca cagiran taraf "kayit yok" sanip eski modeli koruyor
      // (bkz. useContentDetail > fetch). mutate() gibi firlatmiyoruz ama en azindan
      // gorunur kiliyoruz.
      if (result.errors && result.errors.length > 0) {
        console.error('GraphQL query hatasi:', result.errors.map(e => e.message).join('; '));
      }

      return result.data;
    })
    .finally(() => {
      useFixedBarLoading().hide(hashCode(randomNumber));
    });
};

/**
 * @example
 *
 * mutate(
 *    gql`
 *      mutation saveSignature($input: SignatureInput!) {
 *          saveSignature(
 *            input: $input
 *          ) {
 *            id
 *            name
 *            sites
 *            isApproved
 *          }
 *      }
 *    `, {
 *     input: {
 *       id: '5f218b0c44600d0033dbaecc',
 *       name: 'Ahmet Egesel',
 *       sites: ['1'],
 *       isApproved: true,
 *     },
 *   }).then(console.log); */
export const mutate = (mutationString, variables) => {
  const randomNumber = generateRandomNumber();
  useFixedBarLoading().show(hashCode(randomNumber));
  const omitTypename = (key, value) => (key === '__typename' ? undefined : value);

  return createGraphqlClient().mutate({
    mutation: mutationString,
    variables: JSON.parse(JSON.stringify(variables), omitTypename),
  })
    .then(result => {
      // With errorPolicy: 'all', Apollo does NOT throw on GraphQL errors —
      // it returns { data, errors } together (data may be null due to
      // non-null violations). Without this check, callers destructuring
      // result.data crash with "Cannot read properties of null", masking
      // the real server error. Surface it as a proper exception instead.
      if (result.errors && result.errors.length > 0) {
        const messages = result.errors.map(e => e.message).join('; ');
        throw new Error(messages);
      }

      return result.data;
    })
    .finally(() => {
      useFixedBarLoading().hide(hashCode(randomNumber));
    });
};
